<template>
  <div :class="$style['product-stock']">
    <ClientOnly>
      <Teleport to="#teleports">
        <Transition name="modal">
          <Modal
            v-if="showOverlay"
            class="modal--small"
            @close="showOverlay = false">
            <template #modal-header>
              <h4 class="base-font">
                {{ $t('product/pick-a-store') }}
              </h4>
            </template>
            <ShopPicker
              :selected-shop="userStore.preferredShop"
              @select="selectShop" />
          </Modal>
        </Transition>
      </Teleport>
    </ClientOnly>
    <ClientOnly>
      <div
        v-if="loading"
        :class="$style['product-stock__overlay']">
        {{ $t('general/loading') }}
      </div>

      <div
        v-if="userStore.preferredShop?.length > 0"
        :class="$style['product-stock__row']">
        <SvgIcon
          :class="$style['product-stock__icon']"
          name="Buildings/store-2-line" />
        <div>
          {{ $t('product/in-stock-in') }}
          <b>{{ preferredShopTitle }}</b><br>
          <Btn
            class=" h-margin-left-auto"
            size="small"
            color="link"
            no-padding="x"
            @click="showOverlay = true">
            {{ $t('product/pick-other-store') }}
          </Btn>
        </div>
      </div>
      <div
        v-else
        :class="$style['product-stock__row']"
        @click="showOverlay = true">
        <SvgIcon
          :class="$style['product-stock__icon']"
          name="Buildings/store-2-line" />
        <div>
          <div>
            <b>
              {{ $t('product/show-store-stock') }}
            </b>
          </div>
          <Btn
            color="link"
            no-padding="x"
            data-test-id="pick-store-button"
            size="small">
            {{ $t('product/pick-a-store') }}
          </Btn>
        </div>
      </div>
      <div
        v-if="userStore.preferredShop?.length > 0"
        :class="$style['product-stock__row']">
        <span
          v-for="stockItem in stock"
          :key="userStore.preferredShop + '-' + stockItem.size"
          data-test-id="product-stock-badge"
          :title="shopStock(stockItem) < 1 ? $t('general/no') : $t('general/yes')"
          :class="[
            $style['product-stock__badge'],
            {
              [$style['product-stock__badge--unavailable']]:
                shopStock(stockItem) < 1
            }
          ]"
          class="">{{ stockItem.size }}
          <SvgIcon
            v-if="shopStock(stockItem) > 0"
            :class="$style['SvgIcon']"
            name="System/check-line" />
          <SvgIcon
            v-else
            :class="$style['SvgIcon']"
            name="System/close-line" /></span>
      </div>
      <div
        v-else
        :class="$style['product-stock__row']">
        <span
          v-for="stockItem in stock"
          :key="`placeholder-${stockItem.size}`"
          :class="[
            $style['product-stock__badge'],
            $style['product-stock__badge--placeholder']
          ]">
          {{ stockItem.size }}
        </span>
      </div>
    </ClientOnly>
  </div>
</template>
<script setup>
const props = defineProps({
  stock: {
    type: [Array, Object],
    default: null
  }
})

const userStore = useUserStore()
const showOverlay = ref(false)
const loading = ref(true)

const preferredShopTitle = computed(() => {
  if (!props.stock.length) { return }
  return props.stock[0].shops.find(item => {
    return item.name === userStore.preferredShop
  })?.title
})

onMounted(() => {
  loading.value = false
})

const selectShop = shop => {
  userStore.preferredShop = shop
  showOverlay.value = false
}

const shopStock = stock => {
  if (!stock || !stock.shops) { return }
  return stock.shops.find(shop => {
    return shop.name === userStore.preferredShop
  })?.stock
}
</script>
<style lang="scss" module>
.product-stock {
  align-items: flex-start;
  background-color: var(--white);
  border: 1px solid var(--grey-lighter);
  border-radius: var(--border-radius);
  justify-content: flex-start;
  display: flex;
  flex-direction: column;
  padding: calc(var(--spacing) * 2);
  position: relative;
  min-height: calc(var(--spacing) * 16);

  &__overlay {
    align-items: center;
    justify-content: center;
    background-color: var(--white);
    display: flex;
    flex-direction: column;
    font-size: var(--font-size-s);
    color: var(--grey-dark);
    padding: 0 calc(var(--spacing ) * 3);
    position: absolute;
    inset: 0;
    width: auto;
    height: auto;
    z-index: 2;
  }

  &__row {
    align-items: flex-start;
    display: flex;
    flex-flow: row wrap;
    padding: calc(var(--spacing) * 0.5) 0;
    width: 100%;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }

    &--column {
      flex-direction: column;
    }
  }

  &__icon {
    margin-right: calc(var(--spacing ) * 2);
  }

  &__badge {
    align-items: center;
    background-color: var(--grey-lighter);

    // border: 1px solid var(--grey-lighter);
    color: var(--grey-dark);
    display: inline-flex;

    // font-weight: var(--font-weight-bold);
    padding: calc(var(--spacing) * 0.5) var(--spacing);
    margin-right: calc(var(--spacing) * 0.5);
    margin-bottom: calc(var(--spacing) * 0.5);
    font-size: var(--font-size-s);
    line-height: 1.3;

    .SvgIcon {
      margin-left: var(--spacing);
      color: var(--success);
      width: calc(var(--spacing) * 2.5);
      height: calc(var(--spacing) * 2.5);
    }

    &--placeholder {
      border-color: var(--grey-lighter);
      color: var(--grey);
      font-weight: var(--font-weight-normal);

      .SvgIcon {
        color: var(--grey);
      }
    }

    &--unavailable {
      // background-color: var(--red);
      // color: var(--red);
      font-weight: var(--font-weight-normal);

      .SvgIcon {
        color: var(--red);
      }
    }
  }
}
</style>
